import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Info, CreditCard } from "lucide-react";
import { getPaymentIcon } from "@/lib/paymentIcons";
import { createElement } from "react";
import { cn } from "@/lib/utils";
import { formatPrice } from "@/utils/formatPrice";
import type { PlayerPriceResult } from "@/lib/playerPricing";
import type { PaymentMethodOption } from "../paymentTypes";
import PriceSummary from "./PriceSummary";
import DiscountCodeInput from "@/components/booking/DiscountCodeInput";
import CancellationPolicyLine from "@/components/booking/CancellationPolicyLine";
import type { PolicyTier } from "@/lib/cancellationPolicyLine";
import { useTranslation } from "react-i18next";

interface Step3PaymentProps {
  bookingType: "private" | "open_match";
  startTime: Date;
  endTime: Date;
  durationMinutes: number;
  courts: Array<{ courtId: string; courtName: string }>;
  invitedPlayers: Array<{ id: string; full_name: string | null }>;
  splitStrategy: "full" | "per_person";
  totalPrice: number;
  courtMaxPlayers: number;
  playerPrices: PlayerPriceResult[];
  addonsTotal: number;
  currentUserId: string;
  // Payment
  paymentMethod: string;
  onPaymentMethodChange: (method: string) => void;
  availableMethods: PaymentMethodOption[];
  creditBalance: number;
  organizationId?: string;
  discountAmount: number;
  onDiscountChange: (amount: number, codeId: string | null) => void;
  bookingLimitReached?: boolean;
  activeBookingCount?: number;
  maxBookings?: number;
  /**
   * Ingen slot debiterar (charge-villkoret i öre, per slot — se
   * src/lib/bookingChargeGate.ts). Döljer betalsektionen; CTA:n byter till
   * "Boka" i BookingWizard. Aldrig härlett ur det VISADE beloppet (L60).
   */
  isZeroCharge?: boolean;
  /** Hallens settings.cancellation.tiers — policyraden läser per hall. */
  cancellationTiers?: readonly PolicyTier[] | null;
  /** Hallens settings.cancellation.no_show.enabled. */
  noShowEnabled?: boolean;
}

export default function Step3Payment({
  bookingType,
  startTime,
  endTime,
  durationMinutes,
  courts,
  invitedPlayers,
  splitStrategy,
  totalPrice,
  courtMaxPlayers,
  playerPrices,
  addonsTotal,
  currentUserId,
  paymentMethod,
  onPaymentMethodChange,
  availableMethods,
  creditBalance,
  organizationId,
  discountAmount,
  onDiscountChange,
  bookingLimitReached,
  activeBookingCount,
  maxBookings,
  isZeroCharge,
  cancellationTiers,
  noShowEnabled,
}: Step3PaymentProps) {
  const { t } = useTranslation("booking");
  const grossAmount = totalPrice + addonsTotal;
  const isActivity = bookingType === "open_match";
  const confirmedPlayers = playerPrices.length;
  const emptySpots = Math.max(0, courtMaxPlayers - confirmedPlayers);

  // Open match: use creator's individual category price
  const creatorPrice = isActivity && playerPrices.length > 0 ? playerPrices[0] : null;
  const creatorShare = creatorPrice ? creatorPrice.courtShare : Math.ceil(grossAmount / courtMaxPlayers);
  const cappedDiscount = isActivity ? Math.min(discountAmount, creatorShare) : 0;
  const creatorPaysRaw = isActivity ? Math.max(0, creatorShare - cappedDiscount) : 0;
  const creatorPays = isActivity ? (creatorPaysRaw === 0 ? 0 : Math.max(creatorPaysRaw, 3)) : 0;

  const payFullAmount = isActivity ? grossAmount : Math.max(0, grossAmount - discountAmount);
  const pricePerPerson = isActivity ? creatorShare : Math.ceil(payFullAmount / courtMaxPlayers);
  const bookerPays = isActivity
    ? creatorPays
    : pricePerPerson + pricePerPerson * emptySpots;
  const myShareAmount =
    splitStrategy === "full" ? (isActivity ? bookerPays : payFullAmount) : bookerPays;

  const hasEnoughCredits = creditBalance >= myShareAmount;

  const typeIcon = bookingType === "private" ? "🔒" : "🌍";
  const typeLabel =
    bookingType === "private" ? t("privateBooking") : t("openMatch");

  const courtNames =
    courts.length === 1
      ? courts[0].courtName
      : `${courts.length} banor`;

  const playerSummary = (() => {
    const parts: string[] = [t("you")];
    invitedPlayers.forEach((p) => parts.push(p.full_name ?? t("unknown")));
    if (isActivity && emptySpots > 0) {
      parts.push(`${emptySpots} öppna`);
    }
    return parts.join(" + ");
  })();

  return (
    <div className="space-y-5 py-4">
      {/* Max bookings limit warning */}
      {bookingLimitReached && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="text-xs">
            {t("maxBookingsReached", { count: activeBookingCount, max: maxBookings })}
          </AlertDescription>
        </Alert>
      )}

      {/* Summary card */}
      <div className="rounded-lg border p-4 space-y-1.5 text-sm">
        <p className="font-medium">
          {typeIcon} {typeLabel}
        </p>
        <p className="text-muted-foreground">
          {courtNames} &middot;{" "}
          {format(startTime, "EEE d MMM", { locale: sv })}
        </p>
        <p className="text-muted-foreground">
          {format(startTime, "HH:mm")}–{format(endTime, "HH:mm")} (
          {durationMinutes} min)
        </p>
        <p className="text-muted-foreground">{t("playersLabel")}: {playerSummary}</p>
        {!isActivity && invitedPlayers.length > 0 && (
          <p className="text-muted-foreground">
            {t("paymentLabel")}:{" "}
            {splitStrategy === "full" ? t("entireBooking") : t("eachPerson")}
          </p>
        )}
      </div>

      {/* Payment method selection — döljs helt när ingen slot debiterar.
          Gaten är charge-villkoret i öre (isZeroCharge), aldrig det visade
          beloppet (L60). Rabattkoden nedan lämnas KVAR: det kan vara just
          rabatten som gör summan 0, och då måste den gå att ta bort igen. */}
      {!isZeroCharge && (
        // Wrappern bär testid:t så e2e kan asserta FRÅNVARO av hela
        // betalsektionen i ett uttryck. Utan den måste testet räkna upp de tre
        // grenarna nedan (ingen metod / en metod / flera) och skulle tystna så
        // fort en fjärde tillkom. Grenarna renderar ett element var, så
        // wrappern ändrar inte space-y-flödet.
        <div data-testid="payment-method-section">
          {availableMethods.length === 0 ? (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription className="text-xs">
              {t("noPaymentMethod")}
            </AlertDescription>
          </Alert>
          ) : availableMethods.length === 1 ? (
          <div className="flex items-center gap-2 rounded-lg border p-3 text-sm">
            <span>{availableMethods[0].icon}</span>
            <span className="font-medium">{availableMethods[0].label}</span>
            {availableMethods[0].detail && (
              <span className="text-xs text-muted-foreground ml-auto">
                {availableMethods[0].detail}
              </span>
            )}
          </div>
          ) : (
          <div>
            <Label className="text-xs text-muted-foreground mb-1.5 block">
              {t("payWith")}
            </Label>
            <div className="flex gap-2 flex-wrap">
              {availableMethods.map((method) => {
                const isCredits = method.value === "credits";
                const disabled = isCredits && !hasEnoughCredits;
                return (
                  <button
                    key={method.value}
                    onClick={() =>
                      !disabled && onPaymentMethodChange(method.value)
                    }
                    disabled={disabled}
                    className={cn(
                      "flex flex-col items-center gap-1 rounded-lg border px-4 py-3 text-sm transition-colors min-w-[80px] min-h-[44px]",
                      disabled && "opacity-40 cursor-not-allowed",
                      paymentMethod === method.value
                        ? "border-primary bg-primary/5 text-primary"
                        : "border-border hover:bg-muted/50"
                    )}
                  >
                    {createElement(getPaymentIcon(method.icon) ?? CreditCard, { className: "h-5 w-5" })}
                    <span className="font-medium text-xs">{method.label}</span>
                    {method.detail && (
                      <span className="text-[10px] text-muted-foreground">
                        {method.detail}
                      </span>
                    )}
                    {isCredits && !hasEnoughCredits && (
                      <span className="text-[10px] text-destructive">
                        {t("insufficient")}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          )}
        </div>
      )}

      {/* Contextual info — samma gate; vid 0 kr finns ingen betalning att
          informera om. */}
      {!isZeroCharge && payFullAmount > 0 && (
        <>
          {paymentMethod === "stripe" && (
            <Alert className="border-primary/30 bg-primary/5">
              <Info className="h-4 w-4 text-primary" />
              <AlertDescription className="text-xs">
                {splitStrategy === "per_person" && confirmedPlayers > 1
                  ? t("cardPaymentInfoSplit")
                  : t("cardPaymentInfo")}
              </AlertDescription>
            </Alert>
          )}
          {paymentMethod === "credits" && hasEnoughCredits && (
            <Alert className="border-emerald-500/30 bg-emerald-50 dark:bg-emerald-950/20">
              <Info className="h-4 w-4 text-emerald-600" />
              <AlertDescription className="text-xs text-emerald-700 dark:text-emerald-400">
                {t("creditsDeducted", { amount: formatPrice(myShareAmount) })}
              </AlertDescription>
            </Alert>
          )}
          {paymentMethod === "credits" && !hasEnoughCredits && (
            <Alert variant="destructive">
              <Info className="h-4 w-4" />
              <AlertDescription className="text-xs">
                {t("insufficientCredits", { balance: formatPrice(creditBalance), needed: formatPrice(myShareAmount) })}
              </AlertDescription>
            </Alert>
          )}
          {availableMethods.find((m) => m.value === paymentMethod)?.type ===
            "offline" && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription className="text-xs">
                {t("offlinePayNotice")}
              </AlertDescription>
            </Alert>
          )}
        </>
      )}

      {/* Discount code */}
      {organizationId && (
        <DiscountCodeInput
          organizationId={organizationId}
          totalAmount={totalPrice + addonsTotal}
          onDiscountChange={(amount, codeId) => onDiscountChange(amount, codeId)}
        />
      )}
      {isActivity && discountAmount > 0 && (
        <p className="text-xs text-muted-foreground -mt-2">
          {t("discountOpenMatchNote", { applied: formatPrice(cappedDiscount) })}
        </p>
      )}

      {/* Price breakdown */}
      {(splitStrategy === "per_person" ||
        (splitStrategy === "full" && confirmedPlayers > 1)) && (
        <PriceSummary
          playerPrices={playerPrices}
          currentUserId={currentUserId}
          splitStrategy={splitStrategy}
          totalPrice={totalPrice}
          courtMaxPlayers={courtMaxPlayers}
          isActivity={isActivity}
          addonsTotal={addonsTotal}
        />
      )}

      {/* Avbokningsvillkor FÖRE köp — sist i steget, alltså direkt ovanför
          footerns CTA. Visas i ALLA prisfall, även 0 kr: villkoren gäller
          platsen oavsett vad bokningen kostade. Ren display — avgiftsmotorn
          (FEL-006) rörs inte härifrån. */}
      <CancellationPolicyLine
        tiers={cancellationTiers}
        startTime={startTime}
        noShowEnabled={noShowEnabled}
      />
    </div>
  );
}
